import {cellPositions, createGame, Direction, GameState, getTileMovements, moveGame} from './game';

export interface AnimatedTile {
    id: number;
    position: number;
    value: number;
    appearance: 'idle' | 'spawn' | 'merge';
}

export interface MoveInput {
    direction: Direction;
    randomPosition: number;
    randomValue: number;
    animate: boolean;
}

export interface AnimatedGame {
    game: GameState;
    tiles: Array<AnimatedTile>;
    pendingTiles: Array<AnimatedTile>;
    nextId: number;
    phase: 'idle' | 'sliding' | 'settling';
    queue: Array<MoveInput>;
    turn: number;
    scoreGain: number;
}

type AnimationAction =
    | {type: 'move', input: MoveInput}
    | {type: 'finishSlide'}
    | {type: 'finishSettle'}
    | {type: 'restart', game: GameState};

// Keep slideDuration in sync with --slide-duration in styles.sass.
export const slideDuration = 160;
export const settleDuration = 32;
const maxQueuedMoves = 2;

const createTiles = (game: GameState, arrivals: Array<AnimatedTile>, firstId: number): Array<AnimatedTile> => {
    let nextId = firstId;

    return cellPositions.filter(position => game.board[position] !== 0).map(position => {
        const incoming = arrivals.filter(tile => tile.position === position);
        const [survivor] = incoming;
        if (incoming.length === 1 && survivor) return survivor;

        return {
            id: nextId++,
            position,
            value: game.board[position] ?? 0,
            appearance: incoming.length > 0 ? 'merge' : 'spawn',
        };
    });
};

/** Give tiles stable keys; merged and newly spawned tiles get their own entrance animation. */
export const createAnimatedGame = (game: GameState = createGame(), firstId = 0): AnimatedGame => {
    const tiles = createTiles(game, [], firstId);

    return {
        game,
        tiles,
        pendingTiles: [],
        nextId: firstId + tiles.length,
        phase: 'idle',
        queue: [],
        turn: 0,
        scoreGain: 0,
    };
};

const startMove = (state: AnimatedGame, input: MoveInput): AnimatedGame => {
    const {direction, randomPosition, randomValue, animate} = input;
    const game = moveGame(state.game, direction, randomPosition, randomValue);
    if (game === state.game) return state;
    const movements = getTileMovements(state.game.board, direction);
    const moving = state.tiles.map((tile): AnimatedTile => ({
        ...tile,
        appearance: 'idle',
        position: movements.find(movement => movement.from === tile.position)?.to ?? tile.position,
    }));
    const settled = createTiles(game, moving, state.nextId);
    const nextId = settled.reduce((id, tile) => Math.max(id, tile.id + 1), state.nextId);

    return {
        ...state,
        game,
        nextId,
        tiles: animate ? moving : settled,
        pendingTiles: animate ? settled : [],
        phase: animate ? 'sliding' : 'idle',
        turn: state.turn + 1,
        scoreGain: game.score - state.game.score,
    };
};

const playQueuedMoves = (state: AnimatedGame): AnimatedGame => {
    let current: AnimatedGame = {...state, phase: 'idle'};
    while (current.queue.length > 0 && current.phase === 'idle') {
        const [input, ...queue] = current.queue;
        current = {...current, queue};
        if (input) current = startMove(current, input);
    }

    return current;
};

/** Buffer only a couple of inputs so fast play stays responsive without interrupting a slide. */
export const animationReducer = (state: AnimatedGame, action: AnimationAction): AnimatedGame => {
    switch (action.type) {
        case 'move':
            if (state.phase === 'idle') return startMove(state, action.input);
            if (state.queue.length >= maxQueuedMoves) return state;

            return {...state, queue: [...state.queue, action.input]};
        case 'finishSlide':
            if (state.phase !== 'sliding') return state;

            return {...state, tiles: state.pendingTiles, pendingTiles: [], phase: 'settling'};
        case 'finishSettle':
            if (state.phase !== 'settling') return state;

            return playQueuedMoves(state);
        case 'restart':
            return createAnimatedGame(action.game, state.nextId);
    }
};
